import React, { useEffect, useRef } from "react";

/**
 * Custom cursor — a dot and a trailing ring that enlarges on interactive elements.
 * Only active on desktop with fine pointers.
 */
export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const state = useRef({ x: 0, y: 0, rx: 0, ry: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)");
    if (!mq.matches) return;

    const html = document.documentElement;
    html.classList.add("has-cursor");

    const onMove = (e) => {
      state.current.x = e.clientX;
      state.current.y = e.clientY;
    };

    const hotSelector = 'a, button, [role="button"], input, textarea, select, label, [data-cursor-hot]';
    const onOver = (e) => {
      if (e.target.closest && e.target.closest(hotSelector)) {
        html.classList.add("cursor-hot");
      }
    };
    const onOut = (e) => {
      if (e.target.closest && e.target.closest(hotSelector)) {
        html.classList.remove("cursor-hot");
      }
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);

    const loop = () => {
      const s = state.current;
      s.rx += (s.x - s.rx) * 0.18;
      s.ry += (s.y - s.ry) * 0.18;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${s.x}px, ${s.y}px) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${s.rx}px, ${s.ry}px) translate(-50%, -50%)`;
      }
      raf.current = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      html.classList.remove("has-cursor", "cursor-hot");
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="cursor-ring" />
      <div ref={dotRef} className="cursor-dot" />
    </>
  );
}
